var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as THREE from 'three';
import * as TextCreation from '../TextCreation';
import { Orb } from '../../../orbs/Orb';
import { computeRingHierarchy, RingOrbitAnimator, registerRingChild, } from '../../../utils/RingLayout';
import { OrbConnectionManager } from '../../connections/OrbConnectionManager';
function cloneGalleryLayoutOptions(options) {
    if (!options)
        return undefined;
    const clone = Object.assign({}, options);
    if (options.centre)
        clone.centre = options.centre.clone();
    if (options.normal)
        clone.normal = options.normal.clone();
    if (options.planeAxis)
        clone.planeAxis = options.planeAxis.clone();
    return clone;
}
function mergeGalleryLayoutOptions(base, override) {
    if (!base && !override)
        return undefined;
    const merged = {};
    if (base) {
        Object.assign(merged, cloneGalleryLayoutOptions(base));
    }
    if (override) {
        Object.assign(merged, cloneGalleryLayoutOptions(override));
    }
    return merged;
}
function colorToHexString(color, fallback = '#f0f0f0') {
    if (typeof color === 'number') {
        return `#${color.toString(16).padStart(6, '0')}`;
    }
    return fallback;
}
function cloneOrbConfig(config) {
    const layers = config.layers.map(layer => {
        const layerClone = Object.assign({}, layer);
        if (layer.geometryParams) {
            layerClone.geometryParams = Object.assign({}, layer.geometryParams);
        }
        return layerClone;
    });
    const clone = Object.assign(Object.assign({}, config), { layers });
    if (config.textFont)
        clone.textFont = config.textFont;
    clone.text = null;
    return clone;
}
function cloneNodeTextOptions(text) {
    if (!text)
        return undefined;
    return Object.assign(Object.assign({}, text), { offset: text.offset ? text.offset.clone() : undefined });
}
function cloneGalleryNode(node) {
    const cloned = Object.assign(Object.assign({}, node), { config: cloneOrbConfig(node.config), item: node.item
            ? Object.assign(Object.assign({}, node.item), { normal: node.item.normal ? node.item.normal.clone() : undefined, layoutOptions: mergeGalleryLayoutOptions(undefined, node.item.layoutOptions) }) : undefined, text: cloneNodeTextOptions(node.text) });
    if (node.ring) {
        cloned.ring = {
            options: cloneGalleryLayoutOptions(node.ring.options),
            children: node.ring.children.map(child => cloneGalleryNode(child)),
        };
    }
    return cloned;
}
function createGalleryCoreNode(id, label, color, radius = 260, overrides = {}) {
    var _a, _b, _c, _d, _e;
    const { highlightColor = 0xf1faee, highlightOpacity = 0.26, highlightScale = 0.22, layers } = overrides, base = __rest(overrides, ["highlightColor", "highlightOpacity", "highlightScale", "layers"]);
    const config = Object.assign(Object.assign({}, base), { orbType: (_a = base.orbType) !== null && _a !== void 0 ? _a : 'RING_GALLERY_CORE', radius, textColor: (_b = base.textColor) !== null && _b !== void 0 ? _b : 0xf6f8ff, textOutlineColor: (_c = base.textOutlineColor) !== null && _c !== void 0 ? _c : 0x102032, defaultLayerSide: (_d = base.defaultLayerSide) !== null && _d !== void 0 ? _d : THREE.BackSide, defaultLayerScaleFactor: (_e = base.defaultLayerScaleFactor) !== null && _e !== void 0 ? _e : 0.08, layers: layers !== null && layers !== void 0 ? layers : [
            {
                shape: 'sphere',
                color,
                side: THREE.BackSide,
            },
            {
                shape: 'sphere',
                color: highlightColor,
                side: THREE.BackSide,
                transparent: true,
                opacity: highlightOpacity,
                scaleFactor: highlightScale,
            },
        ] });
    return {
        id,
        label,
        config,
    };
}
function createGalleryChildNode(id, label, color, radius = 120, itemOverrides = {}) {
    return {
        id,
        label,
        config: {
            orbType: 'RING_GALLERY_CHILD',
            radius,
            textColor: 0xfdfcff,
            textOutlineColor: 0x121b33,
            defaultLayerSide: THREE.BackSide,
            defaultLayerScaleFactor: 0.05,
            layers: [
                {
                    shape: 'sphere',
                    color,
                    side: THREE.BackSide,
                },
                {
                    shape: 'sphere',
                    color: 0xffffff,
                    side: THREE.BackSide,
                    transparent: true,
                    opacity: 0.18,
                    scaleFactor: 0.12,
                },
            ],
        },
        item: Object.assign({ radius }, itemOverrides),
    };
}
function convertNodesToRingItems(nodes) {
    return nodes.map(node => {
        var _a, _b;
        const itemConfig = node.item;
        const radius = (_a = itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.radius) !== null && _a !== void 0 ? _a : node.config.radius;
        const ringItem = {
            id: node.id,
            radius,
            weight: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.weight,
            angularOffset: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.angularOffset,
            angle: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.angle,
            offsetAlongNormal: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.offsetAlongNormal,
            axisOffset: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.axisOffset,
            absoluteAxisCoordinate: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.absoluteAxisCoordinate,
            angularVelocity: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.angularVelocity,
            clockwise: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.clockwise,
            orbitRadius: itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.orbitRadius,
            normal: (itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.normal) ? itemConfig.normal.clone() : undefined,
        };
        const layoutOptions = mergeGalleryLayoutOptions((_b = node.ring) === null || _b === void 0 ? void 0 : _b.options, itemConfig === null || itemConfig === void 0 ? void 0 : itemConfig.layoutOptions);
        if (layoutOptions) {
            ringItem.layoutOptions = layoutOptions;
        }
        if (node.ring) {
            ringItem.children = convertNodesToRingItems(node.ring.children);
        }
        return ringItem;
    });
}
function instantiateGalleryNode(node, position, scene, animator, arrows, precomputedLayout, parentOrb) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    const message = (_b = (_a = node.text) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : node.label;
    if (message) {
        const font = (_e = (_d = (_c = node.text) === null || _c === void 0 ? void 0 : _c.font) !== null && _d !== void 0 ? _d : node.config.textFont) !== null && _e !== void 0 ? _e : '64px "IBM Plex Sans"';
        const color = typeof ((_f = node.text) === null || _f === void 0 ? void 0 : _f.color) === 'string'
            ? node.text.color
            : colorToHexString(node.config.textColor, '#f7fbff');
        const outline = typeof ((_g = node.text) === null || _g === void 0 ? void 0 : _g.outlineColor) === 'string'
            ? node.text.outlineColor
            : colorToHexString(node.config.textOutlineColor, '#13204b');
        const canvasScale = (_k = (_j = (_h = node.text) === null || _h === void 0 ? void 0 : _h.canvasScale) !== null && _j !== void 0 ? _j : node.config.textCanvasScale) !== null && _k !== void 0 ? _k : 6;
        const textScale = (_m = (_l = node.text) === null || _l === void 0 ? void 0 : _l.scale) !== null && _m !== void 0 ? _m : 1.2;
        const sprite = TextCreation.createTextSprite(message, textScale, undefined, canvasScale, font, color, outline);
        const radius = (_o = node.config.radius) !== null && _o !== void 0 ? _o : 0;
        const defaultOffset = new THREE.Vector3(0, 0, radius > 0 ? radius * 0.68 : radius + 8);
        const offset = (_q = (_p = node.text) === null || _p === void 0 ? void 0 : _p.offset) !== null && _q !== void 0 ? _q : defaultOffset;
        sprite.position.copy(offset);
        node.config.text = sprite;
    }
    else {
        node.config.text = undefined;
    }
    const orb = new Orb(node.config, {}, node.label);
    orb.setPosition(position.clone());
    orb.addToScene(scene);
    if (parentOrb && node.arrowToParent) {
        const profile = {
            style: { color: 0xffc95c, widthPx: 4, opacity: 0.95 },
            arrows: { start: false, end: true, size: 32, facing: 'towardTarget' },
            elastic: { enabled: false },
        };
        arrows.connect({ a: parentOrb, b: orb, profile });
    }
    if (node.ring && node.ring.children.length > 0) {
        const options = (_r = cloneGalleryLayoutOptions(node.ring.options)) !== null && _r !== void 0 ? _r : {};
        options.centre = ((_s = options.centre) !== null && _s !== void 0 ? _s : position.clone()).clone();
        const items = convertNodesToRingItems(node.ring.children);
        const layout = precomputedLayout !== null && precomputedLayout !== void 0 ? precomputedLayout : computeRingHierarchy(items, options);
        layout.placements.forEach((placement, index) => {
            const childNode = node.ring.children[index];
            const childOrb = instantiateGalleryNode(childNode, placement.position.clone(), scene, animator, arrows, placement.childLayout, orb);
            if (childOrb.group) {
                const binding = {
                    object: childOrb.group,
                    placement,
                    centreProvider: () => (orb.group ? orb.group.position.clone() : placement.orbitCentre.clone()),
                };
                animator.add(binding);
                registerRingChild({ parent: orb, child: childOrb, animator, binding });
                if (orb.group) {
                    orb.addDependent(childOrb.group.uuid);
                    childOrb.addDependency(orb.group.uuid);
                }
            }
        });
    }
    return orb;
}
const galleryBaseHeight = 1420;
const ringGalleryExamples = [
    {
        title: '1. Ascending Column',
        subtitle: 'Tiered orbits stepping 1000 units apart above and below the anchor.',
        root: (() => {
            const node = createGalleryCoreNode('gallery-01-core', 'Column', 0x5b8def, 260, {
                highlightColor: 0xaad1ff,
            });
            node.ring = {
                options: {
                    minRadius: 700,
                    minSeparation: 260,
                    angularVelocity: 0.36,
                    planeAxis: new THREE.Vector3(0, 1, 0),
                    normal: new THREE.Vector3(0, 1, 0),
                },
                children: [
                    (() => {
                        const child = createGalleryChildNode('gallery-01-a', 'Upper Crown', 0xffafcc, 150, {
                            offsetAlongNormal: 1000,
                            angularVelocity: 0.58,
                        });
                        child.arrowToParent = true;
                        child.ring = {
                            options: {
                                minRadius: 360,
                                minSeparation: 150,
                                angularVelocity: 0.7,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 1000,
                            },
                            children: [
                                createGalleryChildNode('gallery-01-a1', 'Apex-1', 0xf7ede2, 96, {
                                    offsetAlongNormal: 400,
                                    axisOffset: -600,
                                }),
                                createGalleryChildNode('gallery-01-a2', 'Apex-2', 0xffd6a5, 96, {
                                    offsetAlongNormal: 400,
                                    axisOffset: 0,
                                }),
                                createGalleryChildNode('gallery-01-a3', 'Apex-3', 0xcaffbf, 96, {
                                    offsetAlongNormal: 400,
                                    axisOffset: 600,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-01-b', 'Mid Tier', 0xbde0fe, 150, {
                            axisOffset: 0,
                            angularVelocity: 0.5,
                        });
                        child.ring = {
                            options: {
                                minRadius: 540,
                                minSeparation: 220,
                                angularVelocity: 0.52,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 0,
                            },
                            children: [
                                createGalleryChildNode('gallery-01-b1', 'Middle-1', 0xffc8dd, 110, {
                                    axisOffset: -1000,
                                    offsetAlongNormal: -300,
                                }),
                                createGalleryChildNode('gallery-01-b2', 'Middle-2', 0x90dbf4, 110, {
                                    axisOffset: 0,
                                    offsetAlongNormal: 0,
                                }),
                                createGalleryChildNode('gallery-01-b3', 'Middle-3', 0xa0c4ff, 110, {
                                    axisOffset: 1000,
                                    offsetAlongNormal: 300,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-01-c', 'Lower Garden', 0x80ed99, 150, {
                            offsetAlongNormal: -1000,
                            angularVelocity: 0.46,
                        });
                        child.ring = {
                            options: {
                                minRadius: 420,
                                minSeparation: 200,
                                angularVelocity: 0.6,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: -1000,
                            },
                            children: [
                                createGalleryChildNode('gallery-01-c1', 'Lower-1', 0xb5e48c, 100, {
                                    axisOffset: -500,
                                    offsetAlongNormal: -400,
                                }),
                                createGalleryChildNode('gallery-01-c2', 'Lower-2', 0xfde4cf, 100, {
                                    axisOffset: 500,
                                    offsetAlongNormal: -400,
                                }),
                            ],
                        };
                        return child;
                    })(),
                ],
            };
            return node;
        })(),
    },
    {
        title: '2. Fixed Plane Cascade',
        subtitle: 'Orbit planes locked to absolute heights for layered galleries.',
        root: (() => {
            const node = createGalleryCoreNode('gallery-02-core', 'Cascade', 0x3d5a80, 280, {
                highlightColor: 0x98c1d9,
            });
            node.ring = {
                options: {
                    minRadius: 620,
                    minSeparation: 260,
                    angularVelocity: 0.32,
                    planeAxis: new THREE.Vector3(0, 1, 0),
                    normal: new THREE.Vector3(0, 1, 0),
                    planeOffset: 0,
                },
                children: [
                    (() => {
                        const child = createGalleryChildNode('gallery-02-a', 'Ceiling Row', 0xffb5a7, 150, {
                            absoluteAxisCoordinate: galleryBaseHeight + 1000,
                            angularVelocity: 0.48,
                        });
                        child.arrowToParent = true;
                        child.ring = {
                            options: {
                                minRadius: 380,
                                minSeparation: 180,
                                angularVelocity: 0.66,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 1000,
                            },
                            children: [
                                createGalleryChildNode('gallery-02-a1', 'Ceiling-NE', 0xe5989b, 96, {
                                    offsetAlongNormal: 260,
                                    angularVelocity: 0.7,
                                }),
                                createGalleryChildNode('gallery-02-a2', 'Ceiling-SE', 0xffd166, 96, {
                                    offsetAlongNormal: 260,
                                    angularVelocity: 0.72,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-02-b', 'Gallery Floor', 0x9bf6ff, 150, {
                            absoluteAxisCoordinate: galleryBaseHeight,
                            angularVelocity: 0.42,
                        });
                        child.ring = {
                            options: {
                                minRadius: 520,
                                minSeparation: 220,
                                angularVelocity: 0.5,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 0,
                            },
                            children: [
                                createGalleryChildNode('gallery-02-b1', 'Floor-NW', 0xa1c181, 110, {
                                    axisOffset: -1000,
                                }),
                                createGalleryChildNode('gallery-02-b2', 'Floor-C', 0xffc8dd, 110, {
                                    axisOffset: 0,
                                }),
                                createGalleryChildNode('gallery-02-b3', 'Floor-SE', 0xb388eb, 110, {
                                    axisOffset: 1000,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-02-c', 'Cellar Row', 0x64dfdf, 150, {
                            absoluteAxisCoordinate: galleryBaseHeight - 1000,
                            angularVelocity: 0.5,
                        });
                        child.arrowToParent = true;
                        child.ring = {
                            options: {
                                minRadius: 420,
                                minSeparation: 200,
                                angularVelocity: 0.58,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: -1000,
                            },
                            children: [
                                createGalleryChildNode('gallery-02-c1', 'Cellar-W', 0x80ed99, 96, {
                                    offsetAlongNormal: -260,
                                }),
                                createGalleryChildNode('gallery-02-c2', 'Cellar-E', 0xf72585, 96, {
                                    offsetAlongNormal: -260,
                                }),
                            ],
                        };
                        return child;
                    })(),
                ],
            };
            return node;
        })(),
    },
    {
        title: '3. Tilted Helix',
        subtitle: 'Oblique normal with dependent tiers spiralling downward.',
        root: (() => {
            const node = createGalleryCoreNode('gallery-03-core', 'Helix', 0x9d4edd, 240, {
                highlightColor: 0xd0aaff,
            });
            node.ring = {
                options: {
                    minRadius: 520,
                    minSeparation: 220,
                    angularVelocity: 0.34,
                    normal: new THREE.Vector3(0.35, 0.92, 0.12).normalize(),
                    planeAxis: new THREE.Vector3(0, 1, 0),
                    startAngle: Math.PI * 0.15,
                },
                children: [
                    (() => {
                        const child = createGalleryChildNode('gallery-03-a', 'Helix Tier 1', 0xffd166, 140, {
                            offsetAlongNormal: 1000,
                            angularVelocity: 0.52,
                        });
                        child.ring = {
                            options: {
                                minRadius: 360,
                                minSeparation: 160,
                                angularVelocity: 0.66,
                                normal: new THREE.Vector3(0.4, 0.86, 0.32).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 800,
                            },
                            children: [
                                createGalleryChildNode('gallery-03-a1', 'Helix 1A', 0xffc8dd, 96, {
                                    axisOffset: -800,
                                }),
                                createGalleryChildNode('gallery-03-a2', 'Helix 1B', 0x90dbf4, 96, {
                                    axisOffset: 0,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-03-b', 'Helix Tier 2', 0xf28482, 140, {
                            offsetAlongNormal: 0,
                            angularVelocity: 0.48,
                        });
                        child.arrowToParent = true;
                        child.ring = {
                            options: {
                                minRadius: 420,
                                minSeparation: 200,
                                angularVelocity: 0.6,
                                normal: new THREE.Vector3(0.45, 0.78, -0.44).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 0,
                            },
                            children: [
                                createGalleryChildNode('gallery-03-b1', 'Helix 2A', 0xbde0fe, 96, {
                                    axisOffset: 700,
                                }),
                                createGalleryChildNode('gallery-03-b2', 'Helix 2B', 0xfde4cf, 96, {
                                    axisOffset: -700,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-03-c', 'Helix Tier 3', 0x06d6a0, 140, {
                            offsetAlongNormal: -1000,
                            angularVelocity: 0.56,
                        });
                        child.ring = {
                            options: {
                                minRadius: 340,
                                minSeparation: 160,
                                angularVelocity: 0.68,
                                normal: new THREE.Vector3(-0.25, 0.82, -0.52).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: -800,
                            },
                            children: [
                                createGalleryChildNode('gallery-03-c1', 'Helix 3A', 0xffafcc, 92, {
                                    axisOffset: 0,
                                    offsetAlongNormal: -320,
                                }),
                                createGalleryChildNode('gallery-03-c2', 'Helix 3B', 0xa0c4ff, 92, {
                                    axisOffset: 500,
                                    offsetAlongNormal: -320,
                                }),
                            ],
                        };
                        return child;
                    })(),
                ],
            };
            return node;
        })(),
    },
    {
        title: '4. Vertical Curtain',
        subtitle: 'Rings constrained to a vertical plane sliding along Y.',
        root: (() => {
            const node = createGalleryCoreNode('gallery-04-core', 'Curtain', 0x577590, 260, {
                highlightColor: 0xb9d6f2,
            });
            node.ring = {
                options: {
                    minRadius: 620,
                    minSeparation: 280,
                    angularVelocity: 0.3,
                    normal: new THREE.Vector3(1, 0, 0),
                    planeAxis: new THREE.Vector3(0, 1, 0),
                    planeOffset: 0,
                },
                children: [
                    (() => {
                        const child = createGalleryChildNode('gallery-04-a', 'Curtain Top', 0xffa69e, 150, {
                            axisOffset: 1000,
                            angularVelocity: 0.46,
                        });
                        child.ring = {
                            options: {
                                minRadius: 420,
                                minSeparation: 220,
                                angularVelocity: 0.6,
                                normal: new THREE.Vector3(1, 0.08, 0).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 1000,
                            },
                            children: [
                                createGalleryChildNode('gallery-04-a1', 'Curtain T1', 0xffc8dd, 96, {
                                    absoluteAxisCoordinate: galleryBaseHeight + 1600,
                                }),
                                createGalleryChildNode('gallery-04-a2', 'Curtain T2', 0x90dbf4, 96, {
                                    absoluteAxisCoordinate: galleryBaseHeight + 1000,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-04-b', 'Curtain Mid', 0xcce3de, 150, {
                            axisOffset: 0,
                            angularVelocity: 0.44,
                        });
                        child.arrowToParent = true;
                        child.ring = {
                            options: {
                                minRadius: 560,
                                minSeparation: 230,
                                angularVelocity: 0.58,
                                normal: new THREE.Vector3(1, -0.1, 0.1).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 0,
                            },
                            children: [
                                createGalleryChildNode('gallery-04-b1', 'Curtain M1', 0xffd6a5, 110, {
                                    absoluteAxisCoordinate: galleryBaseHeight + 400,
                                }),
                                createGalleryChildNode('gallery-04-b2', 'Curtain M2', 0xbdb2ff, 110, {
                                    absoluteAxisCoordinate: galleryBaseHeight - 400,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-04-c', 'Curtain Base', 0x00bbf9, 150, {
                            axisOffset: -1000,
                            angularVelocity: 0.48,
                        });
                        child.ring = {
                            options: {
                                minRadius: 420,
                                minSeparation: 210,
                                angularVelocity: 0.62,
                                normal: new THREE.Vector3(1, -0.18, -0.08).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: -1000,
                            },
                            children: [
                                createGalleryChildNode('gallery-04-c1', 'Curtain B1', 0x64dfdf, 96, {
                                    absoluteAxisCoordinate: galleryBaseHeight - 1400,
                                }),
                                createGalleryChildNode('gallery-04-c2', 'Curtain B2', 0xf94144, 96, {
                                    absoluteAxisCoordinate: galleryBaseHeight - 2000,
                                }),
                            ],
                        };
                        return child;
                    })(),
                ],
            };
            return node;
        })(),
    },
    {
        title: '5. Arrow Carousel',
        subtitle: 'Orbiting children that begin with directional edges enabled.',
        root: (() => {
            const node = createGalleryCoreNode('gallery-05-core', 'Carousel', 0xff9f1c, 250, {
                highlightColor: 0xffd07a,
            });
            node.ring = {
                options: {
                    minRadius: 560,
                    minSeparation: 240,
                    angularVelocity: 0.4,
                    planeAxis: new THREE.Vector3(0, 1, 0),
                    normal: new THREE.Vector3(0, 1, 0),
                },
                children: [
                    (() => {
                        const child = createGalleryChildNode('gallery-05-a', 'Arrow North', 0xffc8dd, 140, {
                            axisOffset: 1000,
                            angularVelocity: 0.52,
                        });
                        child.arrowToParent = true;
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-05-b', 'Arrow East', 0x9bf6ff, 140, {
                            offsetAlongNormal: 1000,
                            angularVelocity: 0.52,
                        });
                        child.arrowToParent = true;
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-05-c', 'Arrow South', 0xb388eb, 140, {
                            axisOffset: -1000,
                            angularVelocity: 0.52,
                        });
                        child.arrowToParent = true;
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-05-d', 'Arrow West', 0x64dfdf, 140, {
                            offsetAlongNormal: -1000,
                            angularVelocity: 0.52,
                        });
                        child.arrowToParent = true;
                        return child;
                    })(),
                ],
            };
            return node;
        })(),
    },
    {
        title: '6. Tiered Pinnacle',
        subtitle: 'A tall tree with three cascading layers beneath each branch.',
        root: (() => {
            const node = createGalleryCoreNode('gallery-06-core', 'Pinnacle', 0x184e77, 300, {
                highlightColor: 0x96c5dc,
            });
            node.ring = {
                options: {
                    minRadius: 720,
                    minSeparation: 320,
                    angularVelocity: 0.28,
                    planeAxis: new THREE.Vector3(0, 1, 0),
                    normal: new THREE.Vector3(0, 1, 0),
                },
                children: [
                    (() => {
                        const child = createGalleryChildNode('gallery-06-a', 'Tier One', 0xffb3c6, 160, {
                            offsetAlongNormal: 1000,
                            angularVelocity: 0.46,
                        });
                        child.ring = {
                            options: {
                                minRadius: 420,
                                minSeparation: 200,
                                angularVelocity: 0.6,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 1000,
                            },
                            children: [
                                createGalleryChildNode('gallery-06-a1', 'Tier 1A', 0xffd6a5, 110, {
                                    offsetAlongNormal: 600,
                                }),
                                createGalleryChildNode('gallery-06-a2', 'Tier 1B', 0xcaffbf, 110, {
                                    offsetAlongNormal: 600,
                                    axisOffset: 600,
                                }),
                                createGalleryChildNode('gallery-06-a3', 'Tier 1C', 0xa0c4ff, 110, {
                                    offsetAlongNormal: 600,
                                    axisOffset: -600,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-06-b', 'Tier Two', 0x00bbf9, 160, {
                            offsetAlongNormal: 0,
                            angularVelocity: 0.44,
                        });
                        child.ring = {
                            options: {
                                minRadius: 520,
                                minSeparation: 230,
                                angularVelocity: 0.56,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: 0,
                            },
                            children: [
                                createGalleryChildNode('gallery-06-b1', 'Tier 2A', 0xb388eb, 110, {
                                    offsetAlongNormal: -600,
                                }),
                                createGalleryChildNode('gallery-06-b2', 'Tier 2B', 0xf72585, 110, {
                                    offsetAlongNormal: -600,
                                    axisOffset: 600,
                                }),
                                createGalleryChildNode('gallery-06-b3', 'Tier 2C', 0x80ed99, 110, {
                                    offsetAlongNormal: -600,
                                    axisOffset: -600,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-06-c', 'Tier Three', 0x4361ee, 160, {
                            offsetAlongNormal: -1000,
                            angularVelocity: 0.5,
                        });
                        child.ring = {
                            options: {
                                minRadius: 460,
                                minSeparation: 220,
                                angularVelocity: 0.64,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                                planeOffset: -1000,
                            },
                            children: [
                                createGalleryChildNode('gallery-06-c1', 'Tier 3A', 0xffc8dd, 110, {
                                    offsetAlongNormal: -800,
                                }),
                                createGalleryChildNode('gallery-06-c2', 'Tier 3B', 0x9bf6ff, 110, {
                                    offsetAlongNormal: -800,
                                    axisOffset: 700,
                                }),
                                createGalleryChildNode('gallery-06-c3', 'Tier 3C', 0xb5e48c, 110, {
                                    offsetAlongNormal: -800,
                                    axisOffset: -700,
                                }),
                            ],
                        };
                        return child;
                    })(),
                ],
            };
            return node;
        })(),
    },
    {
        title: '7. Mega Halo',
        subtitle: 'A broad orbit with sub-rings sharing the same elevation.',
        root: (() => {
            const node = createGalleryCoreNode('gallery-07-core', 'Grand Halo', 0x1b9aaa, 320, {
                highlightColor: 0x76d7e4,
            });
            node.ring = {
                options: {
                    minRadius: 2100,
                    minSeparation: 460,
                    angularVelocity: 0.22,
                    planeAxis: new THREE.Vector3(0, 1, 0),
                    normal: new THREE.Vector3(0, 1, 0),
                },
                children: [
                    (() => {
                        const child = createGalleryChildNode('gallery-07-a', 'Halo North', 0xffb703, 180, {
                            axisOffset: 0,
                            offsetAlongNormal: 0,
                            angle: 0,
                        });
                        child.ring = {
                            options: {
                                minRadius: 620,
                                minSeparation: 220,
                                angularVelocity: 0.5,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                            },
                            children: [
                                createGalleryChildNode('gallery-07-a1', 'N-Inner 1', 0xffd6a5, 110, {
                                    offsetAlongNormal: 0,
                                }),
                                createGalleryChildNode('gallery-07-a2', 'N-Inner 2', 0xcaffbf, 110, {
                                    offsetAlongNormal: 0,
                                    angularOffset: (Math.PI * 2) / 3,
                                }),
                                createGalleryChildNode('gallery-07-a3', 'N-Inner 3', 0xa0c4ff, 110, {
                                    offsetAlongNormal: 0,
                                    angularOffset: (Math.PI * 4) / 3,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-07-b', 'Halo East', 0xfb8500, 180, {
                            axisOffset: 0,
                            offsetAlongNormal: 0,
                            angle: Math.PI / 2,
                        });
                        child.ring = {
                            options: {
                                minRadius: 620,
                                minSeparation: 220,
                                angularVelocity: 0.5,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                            },
                            children: [
                                createGalleryChildNode('gallery-07-b1', 'E-Inner 1', 0xbde0fe, 110, {
                                    offsetAlongNormal: 0,
                                }),
                                createGalleryChildNode('gallery-07-b2', 'E-Inner 2', 0xffafcc, 110, {
                                    offsetAlongNormal: 0,
                                    angularOffset: (Math.PI * 2) / 3,
                                }),
                                createGalleryChildNode('gallery-07-b3', 'E-Inner 3', 0x9bf6ff, 110, {
                                    offsetAlongNormal: 0,
                                    angularOffset: (Math.PI * 4) / 3,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-07-c', 'Halo South', 0x219ebc, 180, {
                            axisOffset: 0,
                            offsetAlongNormal: 0,
                            angle: Math.PI,
                        });
                        child.ring = {
                            options: {
                                minRadius: 620,
                                minSeparation: 220,
                                angularVelocity: 0.5,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                            },
                            children: [
                                createGalleryChildNode('gallery-07-c1', 'S-Inner 1', 0xf72585, 110, {
                                    offsetAlongNormal: 0,
                                }),
                                createGalleryChildNode('gallery-07-c2', 'S-Inner 2', 0x64dfdf, 110, {
                                    offsetAlongNormal: 0,
                                    angularOffset: (Math.PI * 2) / 3,
                                }),
                                createGalleryChildNode('gallery-07-c3', 'S-Inner 3', 0xffc8dd, 110, {
                                    offsetAlongNormal: 0,
                                    angularOffset: (Math.PI * 4) / 3,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-07-d', 'Halo West', 0x023047, 180, {
                            axisOffset: 0,
                            offsetAlongNormal: 0,
                            angle: (Math.PI * 3) / 2,
                        });
                        child.ring = {
                            options: {
                                minRadius: 620,
                                minSeparation: 220,
                                angularVelocity: 0.5,
                                planeAxis: new THREE.Vector3(0, 1, 0),
                            },
                            children: [
                                createGalleryChildNode('gallery-07-d1', 'W-Inner 1', 0xffafcc, 110, {
                                    offsetAlongNormal: 0,
                                }),
                                createGalleryChildNode('gallery-07-d2', 'W-Inner 2', 0xb388eb, 110, {
                                    offsetAlongNormal: 0,
                                    angularOffset: (Math.PI * 2) / 3,
                                }),
                                createGalleryChildNode('gallery-07-d3', 'W-Inner 3', 0x80ed99, 110, {
                                    offsetAlongNormal: 0,
                                    angularOffset: (Math.PI * 4) / 3,
                                }),
                            ],
                        };
                        return child;
                    })(),
                ],
            };
            return node;
        })(),
    },
    {
        title: '8. Oblique Terraces',
        subtitle: 'Layered terraces combining fixed planes with tilted normals.',
        root: (() => {
            const node = createGalleryCoreNode('gallery-08-core', 'Terraces', 0x3a86ff, 260, {
                highlightColor: 0x9bf6ff,
            });
            node.ring = {
                options: {
                    minRadius: 640,
                    minSeparation: 260,
                    angularVelocity: 0.3,
                    planeAxis: new THREE.Vector3(0, 1, 0),
                    normal: new THREE.Vector3(0, 1, 0),
                },
                children: [
                    (() => {
                        const child = createGalleryChildNode('gallery-08-a', 'Terrace Upper', 0xffc8dd, 150, {
                            absoluteAxisCoordinate: galleryBaseHeight + 1200,
                            angularVelocity: 0.5,
                        });
                        child.ring = {
                            options: {
                                minRadius: 420,
                                minSeparation: 180,
                                angularVelocity: 0.64,
                                normal: new THREE.Vector3(0.3, 0.9, 0.32).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                            },
                            children: [
                                createGalleryChildNode('gallery-08-a1', 'Upper Edge', 0xffd6a5, 96, {
                                    axisOffset: -800,
                                }),
                                createGalleryChildNode('gallery-08-a2', 'Upper Span', 0xcaffbf, 96, {
                                    axisOffset: 800,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-08-b', 'Terrace Middle', 0x00bbf9, 150, {
                            absoluteAxisCoordinate: galleryBaseHeight,
                            angularVelocity: 0.46,
                        });
                        child.arrowToParent = true;
                        child.ring = {
                            options: {
                                minRadius: 520,
                                minSeparation: 200,
                                angularVelocity: 0.56,
                                normal: new THREE.Vector3(-0.2, 0.95, -0.24).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                            },
                            children: [
                                createGalleryChildNode('gallery-08-b1', 'Middle Edge', 0xbde0fe, 96, {
                                    axisOffset: -600,
                                }),
                                createGalleryChildNode('gallery-08-b2', 'Middle Span', 0xffafcc, 96, {
                                    axisOffset: 600,
                                }),
                            ],
                        };
                        return child;
                    })(),
                    (() => {
                        const child = createGalleryChildNode('gallery-08-c', 'Terrace Lower', 0x8338ec, 150, {
                            absoluteAxisCoordinate: galleryBaseHeight - 1200,
                            angularVelocity: 0.52,
                        });
                        child.ring = {
                            options: {
                                minRadius: 440,
                                minSeparation: 200,
                                angularVelocity: 0.62,
                                normal: new THREE.Vector3(-0.32, 0.86, -0.39).normalize(),
                                planeAxis: new THREE.Vector3(0, 1, 0),
                            },
                            children: [
                                createGalleryChildNode('gallery-08-c1', 'Lower Edge', 0x80ed99, 96, {
                                    axisOffset: -700,
                                }),
                                createGalleryChildNode('gallery-08-c2', 'Lower Span', 0xf94144, 96, {
                                    axisOffset: 700,
                                }),
                            ],
                        };
                        return child;
                    })(),
                ],
            };
            return node;
        })(),
    },
];
export function buildRingGallery(scene) {
    const existing = scene.userData.__ringGalleryAnimators;
    if (Array.isArray(existing)) {
        existing.forEach(anim => anim.dispose());
    }
    const bucket = [];
    scene.userData.__ringGalleryAnimators = bucket;
    if (ringGalleryExamples.length === 0) {
        return;
    }
    const animator = new RingOrbitAnimator();
    bucket.push(animator);
    const arrows = OrbConnectionManager.getInstance();
    const origin = new THREE.Vector3(-8200, galleryBaseHeight, -7400);
    const spacing = 4200;
    const columns = 3;
    ringGalleryExamples.forEach((example, index) => {
        var _a, _b;
        const col = index % columns;
        const row = Math.floor(index / columns);
        const anchor = new THREE.Vector3(origin.x + col * spacing, origin.y, origin.z - row * spacing);
        const rootNode = cloneGalleryNode(example.root);
        const labelMessage = example.subtitle
            ? `${example.title} — ${example.subtitle}`
            : example.title;
        rootNode.label = example.title;
        rootNode.text = (_a = rootNode.text) !== null && _a !== void 0 ? _a : {
            message: labelMessage,
            scale: example.subtitle ? 1.18 : 1.28,
            font: example.subtitle ? '56px "IBM Plex Sans"' : '60px "IBM Plex Sans"',
            color: '#f7fbff',
            outlineColor: '#13204b',
            canvasScale: 6,
        };
        if ((_b = rootNode.ring) === null || _b === void 0 ? void 0 : _b.options) {
            const options = rootNode.ring.options;
            if (typeof options.minRadius === 'number') {
                options.minRadius *= 1.15;
            }
            if (typeof options.minSeparation === 'number') {
                options.minSeparation *= 1.1;
            }
        }
        instantiateGalleryNode(rootNode, anchor.clone(), scene, animator, arrows);
    });
}
